/**
 * CategoryListings - Listing display components for CategoryPage
 */

import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../ui/sheet';
import { Filter, Grid, List, Loader2, Star } from 'lucide-react';
import ListingCard from '../ListingCard';

/**
 * Toolbar with sort and view mode controls
 */
export function ListingsToolbar({
  sortBy,
  viewMode,
  mobileFilterOpen,
  onSortChange,
  onViewModeChange,
  onMobileFilterOpenChange,
  isVehicle,
  onVehicleSearch,
  vehicleFilters,
  MobileFilterContent
}) {
  return (
    <div className="flex items-center justify-between mb-4 bg-white p-3 rounded-lg shadow-sm">
      <div className="flex items-center space-x-2">
        {/* Mobile Filter */}
        <Sheet open={mobileFilterOpen} onOpenChange={onMobileFilterOpenChange}>
          <SheetTrigger asChild>
            <Button variant="outline" size="sm" className="lg:hidden">
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[320px] overflow-y-auto p-0">
            <SheetHeader className="p-4 border-b">
              <SheetTitle>Filters</SheetTitle>
            </SheetHeader>
            <div className="overflow-y-auto max-h-[calc(100vh-80px)]">
              {MobileFilterContent}
            </div>
          </SheetContent>
        </Sheet>

        <Select value={sortBy} onValueChange={(value) => {
          onSortChange(value);
          if (isVehicle && onVehicleSearch) {
            onVehicleSearch(vehicleFilters);
          }
        }}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Most Recent</SelectItem>
            <SelectItem value="relevance">
              <span className="flex items-center gap-1">
                ✨ AI Ranked
              </span>
            </SelectItem>
            <SelectItem value="price-low">Price: Low to High</SelectItem>
            <SelectItem value="price-high">Price: High to Low</SelectItem>
            <SelectItem value="popular">Most Popular</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="hidden sm:flex space-x-1">
        <Button
          variant={viewMode === 'grid' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => onViewModeChange('grid')}
        >
          <Grid className="h-4 w-4" />
        </Button>
        <Button
          variant={viewMode === 'list' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => onViewModeChange('list')}
        >
          <List className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Loading State
 */
export function ListingsLoading() {
  return (
    <div className="flex justify-center items-center py-20">
      <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
    </div>
  );
}

/**
 * Error State
 */
export function ListingsError({ error, onRetry }) {
  return (
    <Card className="p-12 text-center">
      <p className="text-red-500 text-lg mb-4">{error}</p>
      <Button variant="outline" onClick={onRetry}>
        Try Again
      </Button>
    </Card>
  );
}

/**
 * Empty State
 */
export function ListingsEmpty({ onClearFilters }) {
  return (
    <Card className="p-12 text-center mb-8">
      <div className="text-gray-400 mb-4">
        <Filter className="h-16 w-16 mx-auto" />
      </div>
      <p className="text-gray-500 text-lg mb-2">No ads found</p>
      <p className="text-gray-400 text-sm mb-4">
        Try adjusting your filters or browse all categories
      </p>
      <Button variant="outline" onClick={onClearFilters}>
        Clear Filters & Browse All
      </Button>
    </Card>
  );
}

/**
 * Featured Ads Section
 */
export function FeaturedAdsSection({ 
  listings, 
  viewMode,
  showCompare = false,
  compareIds = [],
  onCompareToggle
}) {
  const featuredListings = listings.filter(l => l.featured);
  
  if (featuredListings.length === 0) return null;
  
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <div className="bg-gradient-to-r from-yellow-500 to-orange-500 p-1.5 rounded-lg">
          <Star className="h-4 w-4 text-white fill-white" />
        </div>
        <h2 className="text-lg font-bold text-gray-900">Featured Ads</h2>
        <div className="h-px bg-gradient-to-r from-yellow-500 to-transparent flex-1"></div>
      </div>
      <div className={`grid gap-4 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
        {featuredListings.map(listing => (
          <div key={listing.id} className="relative">
            <div className="absolute -top-2 -right-2 z-10 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-2 py-0.5 rounded-full text-xs font-bold shadow-lg">
              ⭐ FEATURED
            </div>
            <ListingCard 
              listing={listing}
              showCompare={showCompare}
              isCompareSelected={compareIds.includes(listing.id)}
              onCompareToggle={onCompareToggle}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Regular Ads Section
 */
export function RegularAdsSection({ 
  listings, 
  viewMode, 
  hasFeatured,
  showCompare = false,
  compareIds = [],
  onCompareToggle
}) {
  const regularListings = listings.filter(l => !l.featured);
  
  if (regularListings.length === 0) return null;
  
  return (
    <div>
      {hasFeatured && (
        <div className="flex items-center gap-2 mb-3 mt-6">
          <h2 className="text-lg font-bold text-gray-900">All Ads</h2>
          <div className="h-px bg-gray-300 flex-1"></div>
        </div>
      )}
      <div className={`grid gap-4 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
        {regularListings.map(listing => (
          <ListingCard 
            key={listing.id} 
            listing={listing}
            showCompare={showCompare}
            isCompareSelected={compareIds.includes(listing.id)}
            onCompareToggle={onCompareToggle}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Listings Grid - Main component combining featured and regular ads
 */
export function ListingsGrid({ listings, viewMode }) {
  const hasFeatured = listings.some(l => l.featured);
  
  return (
    <>
      <FeaturedAdsSection listings={listings} viewMode={viewMode} />
      <RegularAdsSection listings={listings} viewMode={viewMode} hasFeatured={hasFeatured} />
    </>
  );
}
